import { Injectable, computed, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type Theme = 'light' | 'dark';

const THEME_KEY = 'app-theme';

/**
 * One theme, written two ways — and both are load-bearing.
 *
 * The existing ~27k lines of component SCSS select dark with
 * `[data-theme="dark"]`. Every component ported from the comsky console selects
 * it with `.dark`, because that is what Tailwind's class strategy compiles to.
 * Writing only one of them leaves half the app in the other theme, which is
 * exactly what a first pass at this produced: a slate-dark shell wrapped around
 * blindingly white tables.
 *
 * So `apply()` writes BOTH, always, from the same value. `tailwind.config.js`
 * lists both selectors as dark variants for the same reason, and
 * `design-tokens.scss` defines its dark block against `.dark, [data-theme="dark"]`.
 * Three files, one decision — they only stay consistent because none of them
 * decides anything on its own.
 *
 * The Observable API is kept because the chart, dashboard and geographic-report
 * components subscribe to it; the signal API (`isDark()`, `toggle()`) matches
 * comsky's service so ported components compile unchanged.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly _theme = signal<Theme>(this.initial());
  private readonly themeSubject: BehaviorSubject<Theme>;

  /** Signal API — matches comsky's ThemeService. */
  readonly theme = this._theme.asReadonly();
  readonly isDark = computed(() => this._theme() === 'dark');

  /** Observable API — kept for the existing RxJS consumers. */
  public readonly theme$: Observable<Theme>;

  constructor() {
    this.themeSubject = new BehaviorSubject<Theme>(this._theme());
    this.theme$ = this.themeSubject.asObservable();
    this.applyTheme(this._theme());
  }

  get currentTheme(): Theme {
    return this._theme();
  }

  setTheme(theme: Theme, save: boolean = true): void {
    this._theme.set(theme);
    this.applyTheme(theme);
    this.themeSubject.next(theme);

    if (save) {
      try {
        localStorage.setItem(THEME_KEY, theme);
      } catch {
        /* private window — the theme just doesn't persist */
      }
    }
  }

  /** comsky-compatible alias. */
  set(theme: Theme): void {
    this.setTheme(theme);
  }

  toggleTheme(): void {
    this.setTheme(this.currentTheme === 'light' ? 'dark' : 'light');
  }

  /** comsky-compatible alias. */
  toggle(): void {
    this.toggleTheme();
  }

  private applyTheme(theme: Theme): void {
    const root = document.documentElement;
    const dark = theme === 'dark';

    root.classList.toggle('dark', dark);
    root.setAttribute('data-theme', theme);
    root.style.colorScheme = theme;
  }

  private initial(): Theme {
    try {
      const stored = localStorage.getItem(THEME_KEY);
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    } catch {
      /* private window */
    }

    return 'light';
  }

  resetToSystem(): void {
    try {
      localStorage.removeItem(THEME_KEY);
    } catch {
      /* private window */
    }
    const prefersDark =
      typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
    this.setTheme(prefersDark ? 'dark' : 'light', false);
  }
}
